<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  template: { type: Object, required: true },
  wabaId: { type: String, default: '' },
});

const emit = defineEmits(['edit', 'delete', 'view']);

const { t } = useI18n();

const STATUS_CONFIG = {
  APPROVED: {
    icon: 'i-lucide-circle-check',
    color: 'text-n-teal-11',
    bg: 'bg-n-teal-3',
  },
  PENDING: {
    icon: 'i-lucide-clock',
    color: 'text-n-amber-11',
    bg: 'bg-n-amber-3',
  },
  REJECTED: {
    icon: 'i-lucide-circle-x',
    color: 'text-n-ruby-10',
    bg: 'bg-n-ruby-3',
  },
  PAUSED: {
    icon: 'i-lucide-pause-circle',
    color: 'text-n-amber-11',
    bg: 'bg-n-amber-3',
  },
  DISABLED: {
    icon: 'i-lucide-ban',
    color: 'text-n-slate-11',
    bg: 'bg-n-alpha-2',
  },
};

const CATEGORY_CONFIG = {
  MARKETING: { color: 'text-n-violet-11', bg: 'bg-n-violet-3' },
  UTILITY: { color: 'text-n-teal-11', bg: 'bg-n-teal-3' },
  AUTHENTICATION: { color: 'text-n-blue-11', bg: 'bg-n-blue-3' },
};

const statusConfig = computed(() => {
  const key = props.template.status?.toUpperCase() || 'PENDING';
  return STATUS_CONFIG[key] || STATUS_CONFIG.PENDING;
});

const categoryConfig = computed(() => {
  const key = props.template.category?.toUpperCase() || 'UTILITY';
  return CATEGORY_CONFIG[key] || CATEGORY_CONFIG.UTILITY;
});

const statusLabel = computed(() => {
  const key = props.template.status?.toUpperCase() || 'UNKNOWN';
  return t(`INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.${key}`);
});

const categoryLabel = computed(() => {
  const key = props.template.category?.toUpperCase() || 'UTILITY';
  return t(`INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.${key}`);
});

const bodyText = computed(() => {
  const bodyComponent = props.template.components?.find(c => c.type === 'BODY');
  return bodyComponent?.text || '';
});

const canEdit = computed(() => {
  const status = props.template.status?.toUpperCase();
  return ['APPROVED', 'REJECTED', 'PAUSED'].includes(status);
});

const metaTemplateUrl = computed(
  () =>
    `https://business.facebook.com/wa/manage/message-templates/?waba_id=${props.wabaId}&templates_tab=category_updates`
);

const handleEdit = () => emit('edit', props.template);
const handleDelete = () => emit('delete', props.template);
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-2 hover:bg-n-alpha-1 transition-colors cursor-pointer group"
    @click="$emit('view', template)"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1 min-w-0">
        <span class="text-sm font-semibold text-n-slate-12 group-hover:text-n-brand transition-colors truncate">
          {{ template.name }}
        </span>
        <p class="text-xs text-n-slate-11 line-clamp-2 break-words">
          {{ bodyText }}
        </p>
      </div>
      <div class="flex items-center gap-1 flex-shrink-0" @click.stop>
        <NextButton
          v-if="canEdit"
          v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EDIT_TITLE')"
          xs
          ghost
          slate
          icon="i-lucide-pencil"
          @click="handleEdit"
        />
        <NextButton
          v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.TITLE')"
          xs
          ghost
          slate
          icon="i-lucide-trash-2"
          @click="handleDelete"
        />
        <a
          v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.META_LINK.TOOLTIP')"
          :href="metaTemplateUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-blue-11 transition-colors"
        >
          <span class="i-lucide-external-link text-sm" />
        </a>
      </div>
    </div>
    <div class="flex items-center gap-2 flex-wrap">
      <span
        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md"
        :class="[statusConfig.bg, statusConfig.color]"
      >
        <Icon :icon="statusConfig.icon" class="size-3" />
        {{ statusLabel }}
      </span>
      <span
        class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md"
        :class="[categoryConfig.bg, categoryConfig.color]"
      >
        {{ categoryLabel }}
      </span>
      <span
        class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
      >
        {{ template.language }}
      </span>
    </div>
  </div>
</template>
